// React
import React, { useEffect, useCallback, Fragment } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'

// Components
import FloatingCart from 'store/v2/react/scenes/cart/components/FloatingCart'
import Popover from 'ui/components/Popover'
import PortalWrapper from 'ui/components/PortalWrapper'

// Libraries
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShoppingCart as fasShoppingCart } from '@fortawesome/pro-solid-svg-icons'
import { faShoppingCart as farShoppingCart } from '@fortawesome/pro-regular-svg-icons'
import { faShoppingCart as falShoppingCart } from '@fortawesome/pro-light-svg-icons'
import { faShoppingCart as fatShoppingCart } from '@fortawesome/pro-thin-svg-icons'

import { StyleSheetManager } from 'styled-components'

// Shared
import { setUserFrameworkCartData } from 'shared/utils/userFrameworkEvents'
import ClientActions from 'store/v2/redux/actions/client'
import useIsMobile from 'shared/utils/useIsMobile'
import { shouldForwardProp } from 'shared/utils/shouldForwardProp'

const getIcon = (style) => {
  switch (style) {
    case 'solid':
      return fasShoppingCart
    case 'regular':
      return farShoppingCart
    case 'light':
      return falShoppingCart
    case 'thin':
      return fatShoppingCart
    default:
      return farShoppingCart
  }
}

const CartButton = ({ cart, iconStyle, itemCount, cartOpen, toggleCart, href }) => {
  const isMobile = useIsMobile()

  const icon = getIcon(iconStyle)

  useEffect(() => {
    setUserFrameworkCartData()
  }, [cart])

  const renderFloatingCart = useCallback(
    (props) => (
      <div className="booqable-component">
        <FloatingCart inPortal={!isMobile} href={href} {...props} />
      </div>
    ),
    [isMobile, href]
  )

  const renderIcon = useCallback(
    () => (
      <StyleSheetManager shouldForwardProp={shouldForwardProp} enableVendorPrefixes>
        <button onClick={toggleCart} className="bq-cart-button" part="bq-cart-wrapper">
          <FontAwesomeIcon icon={icon} className="bq-icon-cart" part="bq-icon-cart" />
          {itemCount > 0 && (
            <span className="bq-cart-button__line-count bq-branded" part="bq-cart-lines">
              {itemCount}
            </span>
          )}
        </button>
      </StyleSheetManager>
    ),
    [itemCount, icon, toggleCart]
  )

  if (isMobile) {
    return (
      <Fragment>
        {renderIcon()}
        <PortalWrapper open={cartOpen}>{renderFloatingCart()}</PortalWrapper>
      </Fragment>
    )
  }

  return (
    <Popover
      open={cartOpen}
      onClose={toggleCart}
      method="manual"
      direction="bottom-end"
      content={renderFloatingCart}
      useContentWrapper={false}
      zIndex={9999999}
    >
      {renderIcon()}
    </Popover>
  )
}

CartButton.propTypes = {
  cart: PropTypes.object,
  itemCount: PropTypes.number,
  isMobile: PropTypes.bool,
  toggleCart: PropTypes.func,
  cartOpen: PropTypes.bool,
  iconStyle: PropTypes.oneOf(['solid', 'regular', 'light', 'thin']),
  href: PropTypes.string
}

CartButton.displayName = 'CartButton'

const mapStateToProps = (state, _props) => {
  const cart = state.orm.carts.getById(state.client.cartId)
  const lines = state.orm.lines.getAll({
    id: cart != null ? cart.lines : undefined,
    product_type: ['consumable', 'sales_item', 'rental', 'bundle']
  })
  const cartIsOpen = state.client.cartOpen

  const hasDates = !!((cart != null ? cart.starts_at : undefined) && (cart != null ? cart.stops_at : undefined))

  const itemCount = lines.map((line) => line.quantity).reduce((a, b) => a + b, 0)

  return {
    cart,
    itemCount,
    hasDates,
    cartOpen: state.client.cartOpen,
    locations: state.orm.locations.getAll().sort('name'),
    cartIsOpen
  }
}

const mapDispatchToProps = (_dispatch, _props) => {
  return {
    toggleCart: () => {
      const state = window.booqableStore.getState()
      const isOpen = state.client.cartOpen

      return ClientActions.toggleCart(!isOpen)
    }
  }
}

export default reduxConnect(mapStateToProps, mapDispatchToProps)(CartButton)
